import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Send, AlertTriangle, ShieldAlert, FileText, ClipboardCheck, Clock,
  ChevronRight, X, RefreshCw, CheckCircle2, XCircle, Info, ArrowRight,
  ListChecks, Eye, CornerDownRight, Zap, ThumbsUp, ThumbsDown,
  Globe, Package, RotateCw, Radio, Hash
} from 'lucide-react';
import {
  PredictiveLabel, RiskBadge, ConfidenceText, FeedbackButtons,
  StatusChip, SlidePanel, Timeline, EnvironmentBadge, TransportStatusChip,
  ExternalSourceBadge, RawResponseViewer, DenialStateBadge,
  fmt, _typeLabel, _fmtK, _buildFocusSummary
} from './RcmShared';

function PostingTab() {
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/rcm/remittance/posting/queue'); setQueue(r.data.queue || []); setSummary(r.data.summary || {}); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAction = async (lineId, action) => {
    try {
      if (action === 'approve') {
        await api.post(`/rcm/remittance/posting/${lineId}/mark-ready`, { notes });
      } else if (action === 'apply_payment') {
        await api.post(`/rcm/remittance/lines/${lineId}/apply-payment`);
      } else {
        await api.post(`/rcm/remittance/posting/${lineId}/flag-exception`, { notes });
      }
      setNotes('');
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const batchApply = async () => {
    const approvedIds = queue.filter(l => l.posting_status === 'approved_for_posting').map(l => l.id);
    if (approvedIds.length === 0) { alert('No approved lines to post'); return; }
    try {
      const r = await api.post('/rcm/remittance/lines/batch-apply', { line_ids: approvedIds });
      alert(`Posted ${r.data.posted?.length || 0} payments (${r.data.failed?.length || 0} failed)`);
      load();
    } catch(e) { alert(e.response?.data?.detail || 'Batch apply failed'); }
  };

  const reviewCount = (summary.ready_for_posting_review || 0) + (summary.matched_needs_review || 0);
  const exceptionCount = (summary.posting_exception || 0) + (summary.held_unmatched || 0);

  return (
    <div className="space-y-4" data-testid="posting-tab">
      {/* Banner — this is review, not auto-posting */}
      <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-2 flex items-center gap-2 text-xs" data-testid="posting-review-banner">
        <Info className="h-3.5 w-3.5 text-sky-600 shrink-0" />
        <span className="text-sky-800">Posting Review — Review and approve remittance lines before posting. No automatic posting occurs.</span>
      </div>

      <div className="flex gap-3">
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-sky-600">{reviewCount}</p><p className="text-[10px] text-muted-foreground uppercase">Needs Review</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-red-600">{exceptionCount}</p><p className="text-[10px] text-muted-foreground uppercase">Exceptions</p>
        </CardContent></Card>
        <Card className="flex-1"><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{summary.approved_for_posting || 0}</p><p className="text-[10px] text-muted-foreground uppercase">Approved</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-1 pt-3 px-3 flex flex-row justify-between items-center">
          <CardTitle className="text-xs">Posting Queue ({queue.length})</CardTitle>
          {queue.some(l => l.posting_status === 'approved_for_posting') && (
            <Button size="sm" className="h-6 text-[10px] bg-blue-600 text-white hover:bg-blue-700" onClick={batchApply} data-testid="batch-apply-btn">
              Apply All Approved ({queue.filter(l => l.posting_status === 'approved_for_posting').length})
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={load}><RefreshCw className="h-3 w-3 mr-1" />Refresh</Button>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {queue.length === 0 ? <p className="text-xs text-muted-foreground py-4 text-center">No items in posting queue</p> : (
          <table className="w-full text-xs" data-testid="posting-table">
            <thead><tr className="border-b text-left">
              <th className="py-1.5 font-medium text-muted-foreground">Match</th>
              <th className="py-1.5 font-medium text-muted-foreground">Confidence</th>
              <th className="py-1.5 font-medium text-muted-foreground">Source</th>
              <th className="py-1.5 font-medium text-muted-foreground">Payer</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Expected</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Actual Paid</th>
              <th className="py-1.5 font-medium text-muted-foreground text-right">Variance</th>
              <th className="py-1.5 font-medium text-muted-foreground">Status</th>
              <th className="py-1.5 font-medium text-muted-foreground">Actions</th>
            </tr></thead>
            <tbody>{queue.map((l) => {
              const eva = l._expected_vs_actual || {};
              const canAct = ['ready_for_posting_review', 'matched_needs_review'].includes(l.posting_status);
              return (
                <tr key={l.id} className={`border-b ${l.posting_status === 'posting_exception' || l.posting_status === 'held_unmatched' ? 'bg-red-50/30' : ''}`}>
                  <td className="py-1.5"><StatusChip status={l.matching_status} size="xs" /></td>
                  <td className="py-1.5">
                    <span className={`font-medium ${l.matching_confidence === 'high' ? 'text-emerald-600' : l.matching_confidence === 'medium' ? 'text-amber-600' : 'text-red-600'}`}>
                      {l.matching_confidence}
                    </span>
                  </td>
                  <td className="py-1.5">
                    {l.raw_cpt_code && <span className="font-mono text-[10px] bg-gray-100 rounded px-1" data-testid="posting-source-ref">{l.raw_cpt_code}</span>}
                  </td>
                  <td className="py-1.5">{l._payer_name || '—'}</td>
                  <td className="py-1.5 text-right">{fmt(eva.expected)}</td>
                  <td className="py-1.5 text-right font-medium">{fmt(eva.actual_paid)}</td>
                  <td className={`py-1.5 text-right font-medium ${eva.variance > 500 ? 'text-amber-600' : eva.variance > 0 ? 'text-muted-foreground' : 'text-emerald-600'}`}>
                    {fmt(eva.variance)}
                  </td>
                  <td className="py-1.5"><StatusChip status={l.posting_status} size="xs" /></td>
                  <td className="py-1.5">
                    {canAct && (
                      <div className="flex gap-1">
                        <Button size="sm" className="h-5 text-[10px] px-2" variant="outline" onClick={() => doAction(l.id, 'approve')} data-testid={`approve-${l.id}`}>
                          <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" />Approve
                        </Button>
                        <Button size="sm" className="h-5 text-[10px] px-2" variant="destructive" onClick={() => doAction(l.id, 'exception')} data-testid={`exception-${l.id}`}>
                          <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />Exception
                        </Button>
                      </div>
                    )}
                    {l.posting_status === 'approved_for_posting' && (
                      <Button size="sm" className="h-5 text-[10px] px-2 bg-blue-600 text-white hover:bg-blue-700" onClick={() => doAction(l.id, 'apply_payment')} data-testid={`post-payment-${l.id?.slice(0,8)}`}>
                        Apply Payment
                      </Button>
                    )}
                    {l.posting_status === 'posted' && (
                      <span className="text-[10px] text-emerald-600 font-medium">Posted {l.posted_at?.slice(0,10)}</span>
                    )}
                    {l.posting_decision_snapshot && (
                      <span className="text-[10px] text-muted-foreground">Reviewed {l.posting_reviewed_at?.slice(0,10)}</span>
                    )}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>)}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════
// FOLLOW-UP TAB (Group 3)
// ═══════════════════════════════════════

export { PostingTab };
